import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  AcademicCapIcon,
  UserGroupIcon,
  ListBulletIcon,
  PlusIcon,
  ChartBarIcon,
  CalendarIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";

/**
 * CompetencySidebar - Sidebar navigation for competency management
 * Similar structure to DocumentSidebar for consistency
 */
export const CompetencySidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const sidebarRef = useRef(null);
  
  const [expandedSections, setExpandedSections] = useState(new Set([0, 1, 2, 3, 4, 5])); // Default all expanded

  const menuSections = [
    {
      title: t("Hjelp"),
      icon: InformationCircleIcon,
      items: [
        {
          title: t("Kompetanse Guide"),
          path: "/dashboard/hse-ks/competency/guide",
          icon: InformationCircleIcon,
        },
      ],
    },
    {
      title: t("Kompetansedefinisjoner"),
      icon: DocumentIcon,
      items: [
        {
          title: t("Alle definisjoner"),
          path: "/dashboard/hse-ks/competency/definitions",
          icon: ListBulletIcon,
        },
        {
          title: t("Opprett ny definisjon"),
          path: "/dashboard/hse-ks/competency/definitions/new",
          icon: PlusIcon,
        },
      ],
    },
    {
      title: t("Roller og matrise"),
      icon: UserGroupIcon,
      items: [
        {
          title: t("Kompetansematrise"),
          path: "/dashboard/hse-ks/competency/matrix",
          icon: ChartBarIcon,
        },
        {
          title: t("Administrer roller"),
          path: "/dashboard/hse-ks/competency/roles",
          icon: UserGroupIcon,
        },
        {
          title: t("Opprett ny rolle"),
          path: "/dashboard/hse-ks/competency/roles/new",
          icon: PlusIcon,
        },
      ],
    },
    {
      title: t("Ansatte"),
      icon: UserGroupIcon,
      items: [
        {
          title: t("Ansattoversikt"),
          path: "/dashboard/hse-ks/competency/employees",
          icon: ListBulletIcon,
        },
        {
          title: t("Utløper snart"),
          path: "/dashboard/hse-ks/competency/expiring",
          icon: CalendarIcon,
        },
      ],
    },
    {
      title: t("Kurs"),
      icon: AcademicCapIcon,
      items: [
        {
          title: t("Alle kurs"),
          path: "/dashboard/hse-ks/competency/courses",
          icon: AcademicCapIcon,
        },
        {
          title: t("Opprett nytt kurs"),
          path: "/dashboard/hse-ks/competency/courses/new",
          icon: PlusIcon,
        },
      ],
    },
    {
      title: t("Planlegging"),
      icon: CalendarIcon,
      items: [
        {
          title: t("Opplæringsplan"),
          path: "/dashboard/hse-ks/competency/training-plan",
          icon: CalendarIcon,
        },
      ],
    },
  ];

  const toggleSection = (sectionIndex) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionIndex)) {
      newExpanded.delete(sectionIndex);
    } else {
      newExpanded.add(sectionIndex);
    }
    setExpandedSections(newExpanded);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <div ref={sidebarRef} className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
        <div className="flex items-center mb-2">
          <Cog6ToothIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-sm font-semibold text-gray-900">{t("Administrasjon")}</h3>
        </div>
        <p className="text-xs text-gray-500">
          {t("Navigasjon")}
        </p>
      </div>

      {/* Menu Sections */}
      <div className="p-2">
        {menuSections.map((section, sectionIndex) => {
          const isExpanded = expandedSections.has(sectionIndex);
          const hasActiveItem = section.items.some(item => isActive(item.path));

          return (
            <div key={sectionIndex} className="mb-1">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(sectionIndex)}
                className={`w-full flex items-center justify-between px-2 py-2 text-left hover:bg-gray-50 rounded transition-colors ${
                  hasActiveItem ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <section.icon className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {section.title}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
              </button>

              {/* Section Items */}
              {isExpanded && (
                <div className="ml-6 mt-1 space-y-0.5">
                  {section.items.map((item, itemIndex) => {
                    const active = isActive(item.path);
                    
                    return (
                      <Link
                        key={itemIndex}
                        to={item.path}
                        className={`flex items-center px-2 py-1.5 rounded text-sm transition-colors ${
                          active
                            ? "bg-blue-100 text-blue-700 font-medium"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <item.icon className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                        <span className="truncate">{item.title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

